import type { WaveDef, SectionDef } from '@/data/stages/stageConfig';
import type { StageVariantRoster } from '@/data/enemyVariants';
import { pickWaveVariants, extractBaseIds, type WaveVariantAssignment } from './WaveVariantPicker';

/**
 * WaveManager — pure, no Phaser import (must remain node-testable).
 *
 * Tracks current section + wave-within-section for a stage.
 * StageScene reads currentWave() to know which enemies to spawn,
 * calls enemyDefeated() on each KO, and calls advance() once the
 * wave is cleared. advance() returns an AdvanceResult:
 *   'wave'            — next wave within same section (spawn it)
 *   'section-cleared' — last wave of a section done; moved to next
 *                       section's first wave (show checkpoint banner)
 *   'stage-complete'  — all sections done (spawn boss or clear stage)
 *
 * Back-compat: pass a single-element SectionDef array wrapping flat waves.
 */
export type AdvanceResult = 'wave' | 'section-cleared' | 'stage-complete';

export class WaveManager {
  private readonly sections: SectionDef[];

  private readonly countMult: number;

  private readonly roster: StageVariantRoster;

  private sectionIdx: number;

  private waveIdx: number;

  private remaining: number;

  private variantAssignment: WaveVariantAssignment;

  /**
   * @param sections      Ordered section definitions (each holds its own waves).
   * @param countMult     Enemy count multiplier from difficultyMods (default 1).
   * @param startSection  Section index to start from (checkpoint resume).
   * @param roster        Optional per-stage variant roster. When provided,
   *                      `currentVariantAssignment()` returns a per-wave mix
   *                      that differs from the previous wave for every archetype
   *                      with ≥2 variants.
   */
  constructor(
    sections: SectionDef[],
    countMult = 1,
    startSection = 0,
    roster: StageVariantRoster = {},
  ) {
    this.sections = sections;
    this.countMult = countMult;
    this.roster = roster;
    this.sectionIdx = Math.max(0, Math.min(startSection, sections.length - 1));
    this.waveIdx = 0;
    this.remaining = WaveManager.countEnemies(sections[this.sectionIdx]?.waves[0], countMult);
    // Seed the first wave's variant assignment with no prior wave.
    this.variantAssignment = this.computeVariantAssignment({}, 0);
  }

  private static countEnemies(wave: WaveDef | undefined, mult = 1): number {
    if (!wave) return 0;
    return wave.enemies.reduce((sum, e) => sum + Math.max(1, Math.round(e.count * mult)), 0);
  }

  private computeVariantAssignment(
    prev: WaveVariantAssignment,
    globalWaveIdx: number,
  ): WaveVariantAssignment {
    const wave = this.sections[this.sectionIdx]?.waves[this.waveIdx];
    if (!wave) return {};
    return pickWaveVariants(extractBaseIds(wave.enemies), this.roster, prev, globalWaveIdx);
  }

  currentWave(): WaveDef {
    return this.sections[this.sectionIdx].waves[this.waveIdx];
  }

  /**
   * Returns the variant assignment for the current wave: maps each archetype
   * baseId present in the wave to a variantId from the stage roster.
   * Empty when no roster was supplied.
   */
  currentVariantAssignment(): WaveVariantAssignment {
    return this.variantAssignment;
  }

  /** Global wave index across all sections (for HUD wave display). */
  currentIndex(): number {
    let idx = 0;
    for (let s = 0; s < this.sectionIdx; s += 1) {
      idx += this.sections[s].waves.length;
    }
    return idx + this.waveIdx;
  }

  currentSectionIndex(): number {
    return this.sectionIdx;
  }

  totalWaves(): number {
    return this.sections.reduce((sum, s) => sum + s.waves.length, 0);
  }

  remainingInWave(): number {
    return this.remaining;
  }

  isLastWave(): boolean {
    const lastSection = this.sections[this.sections.length - 1];
    return (
      this.sectionIdx === this.sections.length - 1 && this.waveIdx >= lastSection.waves.length - 1
    );
  }

  isStageComplete(): boolean {
    return this.sectionIdx >= this.sections.length;
  }

  allWavesCleared(): boolean {
    return this.isStageComplete();
  }

  enemyDefeated(): void {
    if (this.remaining > 0) this.remaining -= 1;
  }

  /** Advance to the next wave or section. Returns an AdvanceResult. */
  advance(): AdvanceResult {
    if (this.isStageComplete()) return 'stage-complete';

    const section = this.sections[this.sectionIdx];
    const isLastWaveInSection = this.waveIdx >= section.waves.length - 1;
    const isLastSection = this.sectionIdx >= this.sections.length - 1;

    if (isLastWaveInSection && isLastSection) {
      this.sectionIdx = this.sections.length; // mark complete
      this.remaining = 0;
      return 'stage-complete';
    }

    const prevAssignment = this.variantAssignment;
    const prevGlobalIdx = this.currentIndex();

    if (isLastWaveInSection) {
      this.sectionIdx += 1;
      this.waveIdx = 0;
      this.remaining = WaveManager.countEnemies(
        this.sections[this.sectionIdx].waves[0],
        this.countMult,
      );
      this.variantAssignment = this.computeVariantAssignment(prevAssignment, prevGlobalIdx + 1);
      return 'section-cleared';
    }

    this.waveIdx += 1;
    this.remaining = WaveManager.countEnemies(section.waves[this.waveIdx], this.countMult);
    this.variantAssignment = this.computeVariantAssignment(prevAssignment, prevGlobalIdx + 1);
    return 'wave';
  }

  /**
   * Returns the stage-level elite ID if the current global wave index matches
   * `eliteWaveIndex`. StageScene calls this after spawning each wave to know
   * whether to also spawn the elite.
   *
   * @param eliteId        StageConfig.eliteId
   * @param eliteWaveIndex StageConfig.eliteWaveIndex
   */
  stageEliteIdForCurrentWave(
    eliteId: string | undefined,
    eliteWaveIndex: number | undefined,
  ): string | undefined {
    if (!eliteId || eliteWaveIndex === undefined) return undefined;
    return this.currentIndex() === eliteWaveIndex ? eliteId : undefined;
  }

  /**
   * Returns the section-level elite ID if the current wave is the LAST wave of
   * a non-terminal section that has an `eliteId`. Call this BEFORE calling
   * `advance()` — once the wave clears, StageScene spawns the section elite,
   * then calls `advance()` to move to the next section.
   *
   * Returns `undefined` for the last section (boss/gauntlet triggers instead).
   */
  eliteIdForSectionAboutToClear(): string | undefined {
    if (this.isStageComplete()) return undefined;
    const section = this.sections[this.sectionIdx];
    const isLastWaveInSection = this.waveIdx >= section.waves.length - 1;
    if (!isLastWaveInSection) return undefined;
    const isLastSection = this.sectionIdx >= this.sections.length - 1;
    if (isLastSection) return undefined;
    return section.eliteId;
  }

  /**
   * Reset to the start of a given section (checkpoint respawn).
   * Used when a player loses a life: StageScene calls this with
   * GameState.current.checkpointSection to restart the current section.
   */
  resetToSection(sectionIndex: number): void {
    this.sectionIdx = Math.max(0, Math.min(sectionIndex, this.sections.length - 1));
    this.waveIdx = 0;
    this.remaining = WaveManager.countEnemies(
      this.sections[this.sectionIdx]?.waves[0],
      this.countMult,
    );
    this.variantAssignment = this.computeVariantAssignment({}, 0);
  }
}
